import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select } from './custom-select';
import {
  SingleValue,
  components,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import './language-select.css';

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageOption {
  value: string;
  label: string;
  flag: string;
}

interface LanguageSelectProps {
  languages: Language[];
  currentLanguage: string;
  onLanguageChange: (langCode: string) => void;
  collapsed?: boolean;
  position?: 'top' | 'bottom';
  align?: 'left' | 'right';
}

const CustomOption = (props: OptionProps<LanguageOption, false>) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        <span className="text-base">{props.data.flag}</span>
        <span>{props.data.label}</span>
      </div>
    </components.Option>
  );
};

const CustomSingleValue = (props: SingleValueProps<LanguageOption, false>) => {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <span className="text-base">{props.data.flag}</span>
        <span>{props.data.label}</span>
      </div>
    </components.SingleValue>
  );
};

export const LanguageSelect = ({
  languages,
  currentLanguage,
  onLanguageChange,
  collapsed = false,
  position = 'bottom',
}: LanguageSelectProps) => {
  const { t } = useTranslation();

  const options: LanguageOption[] = languages.map((lang) => ({
    value: lang.code,
    label: lang.name.toUpperCase(),
    flag: lang.flag,
  }));

  const currentValue =
    options.find((opt) => opt.value === currentLanguage) || null;

  const handleChange = (selected: SingleValue<LanguageOption>) => {
    if (selected) {
      onLanguageChange(selected.value);
    }
  };

  if (collapsed) {
    return (
      <div className="language-select-collapsed">
        <Select
          options={options}
          value={currentValue}
          onChange={handleChange}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
            DropdownIndicator: () => <Globe className="w-4 h-4" />,
            IndicatorSeparator: () => null,
          }}
          menuPlacement={position === 'top' ? 'top' : 'bottom'}
          isSearchable={false}
          className="language-select"
          classNamePrefix="language-select"
          placeholder={t('sidebar.select_language')}
          styles={{
            control: (base) => ({
              ...base,
              minWidth: '40px',
              width: '40px',
              height: '40px',
              minHeight: '40px',
              border: '1px solid hsl(var(--border)) !important',
              backgroundColor: 'var(--background)',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'var(--muted)',
                borderColor: 'var(--border)',
              },
            }),
            valueContainer: () => ({
              display: 'none',
            }),
            menuList: (base) => ({
              ...base,
              backgroundColor: 'hsl(var(--card)) !important',
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isSelected
                ? 'hsl(var(--primary))'
                : state.isFocused
                  ? 'var(--muted)'
                  : 'transparent',
              color: state.isSelected
                ? 'hsl(var(--primary-foreground))'
                : 'var(--foreground)',
              cursor: 'pointer',
              '&:active': {
                backgroundColor: 'hsl(var(--primary))',
              },
            }),
          }}
        />
      </div>
    );
  }

  return (
    <div className="language-select-expanded">
      <Select
        options={options}
        value={currentValue}
        onChange={handleChange}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          IndicatorSeparator: () => null,
        }}
        menuPlacement={position === 'top' ? 'top' : 'bottom'}
        isSearchable={false}
        className="language-select"
        classNamePrefix="language-select"
        placeholder={t('sidebar.select_language')}
        styles={{
          control: (base) => ({
            ...base,
            border: '1px solid hsl(var(--border)) !important',
            backgroundColor: 'transparent',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'var(--muted)',
              borderColor: 'var(--border)',
            },
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
            backgroundColor: 'hsl(var(--card)) !important',
            border: '1px solid var(--border)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }),
          menuList: (base) => ({
            ...base,
            backgroundColor: 'hsl(var(--card)) !important',
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? 'hsl(var(--primary))'
              : state.isFocused
                ? 'hsl(var(--muted))'
                : 'transparent',
            color: state.isSelected
              ? 'hsl(var(--primary-foreground))'
              : 'var(--foreground)',
            cursor: 'pointer',
            '&:active': {
              backgroundColor: 'hsl(var(--primary))',
            },
          }),
          singleValue: (base) => ({
            ...base,
            color: 'var(--foreground)',
          }),
        }}
      />
    </div>
  );
};
